import { Injectable, inject } from '@angular/core';
import { WebSocketMessage } from '../../../core/services/websocket.service';
import { Role, Permission, UserRole } from '../../../stores/permission.store';
import { LoggerService } from '../../../core/services/logger.service';
import { PermissionWsEnvelopeSchema } from './permission-websocket-schemas';

/**
 * Permission status message types from backend
 */
export type PermissionStatusMessageType =
  | 'role_created'
  | 'role_updated'
  | 'role_deleted'
  | 'permission_granted'
  | 'permission_revoked'
  | 'user_role_assigned'
  | 'user_role_removed'
  | 'permissions_updated'
  | 'cache_invalidated';

/**
 * Sentinel UUID used when entityId cannot be determined from message
 * This is a valid UUID v4 with all zeros, making it distinguishable
 * from real entity IDs while remaining a valid UUID format
 */
export const UNKNOWN_ENTITY_ID = '00000000-0000-0000-0000-000000000000';

/**
 * Permission status message from WebSocket
 */
export interface PermissionStatusMessage {
  type: PermissionStatusMessageType;
  entityId: string;
  entityType: 'role' | 'permission' | 'user_role';
  data?: Role | Permission | UserRole | null;
  userId?: string;
  roleId?: string;
  timestamp: string;
  initiatedBy?: string;
}

/**
 * Adapter for transforming raw WebSocket messages to PermissionStatusMessage
 * Handles multiple backend message formats for flexibility
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionWebSocketMessageAdapter {
  private readonly logger = inject(LoggerService);

  private readonly validTypes: PermissionStatusMessageType[] = [
    'role_created',
    'role_updated',
    'role_deleted',
    'permission_granted',
    'permission_revoked',
    'user_role_assigned',
    'user_role_removed',
    'permissions_updated',
    'cache_invalidated'
  ];

  /**
   * Strict UUID v4 regex (lowercase only per RFC 4122)
   * Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
   * Where x is any hex digit (0-9a-f) and y is one of 8, 9, a, or b
   */
  private readonly UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

  /**
   * Check if a WebSocket message is a permission status message
   */
  isPermissionStatusMessage(message: WebSocketMessage): boolean {
    const data = message.data as { type?: string } | null | undefined;
    return this.validTypes.includes(message.type as PermissionStatusMessageType) ||
           this.validTypes.includes(data?.type as PermissionStatusMessageType);
  }

  /**
   * Transform raw WebSocket message to PermissionStatusMessage
   * Handles multiple backend formats:
   * - { type, entityId, entityType, data, timestamp }
   * - { type, role_id, user_id, data, timestamp }
   * - { type, id, ... }
   */
  toPermissionStatusMessage(message: WebSocketMessage): PermissionStatusMessage | null {
    // GAP-FE-003 / F-045: gate the wire boundary with Zod before the
    // priority-ordered field extraction below. This only rejects outright
    // shape violations (e.g. a non-object/array `data` payload) — the
    // extraction logic still reconciles every historical field-name variant
    // the backend has emitted (entityId/entity_id, roleId/role_id, ...).
    const envelope = PermissionWsEnvelopeSchema.safeParse(message);
    if (!envelope.success) {
      this.logger.warn(
        'PermissionWebSocketMessageAdapter: WS payload failed Zod validation; ignoring',
        { issues: envelope.error.issues, rawMessage: message },
      );
      return null;
    }

    // Try nested data structure first
    if (message.data && typeof message.data === 'object') {
      const dataBag = message.data as Record<string, unknown>;
      const type = this.extractType(message);
      const entityId = this.extractEntityId(dataBag);
      const entityType = this.extractEntityType(type);
      const data = this.extractData(dataBag);
      const timestamp = this.extractTimestamp(message);
      const initiatedBy = this.extractInitiatedBy(dataBag);
      const userId = this.extractUserId(dataBag);
      const roleId = this.extractRoleId(dataBag);

      if (entityId && type) {
        return { type, entityId, entityType, data, timestamp, initiatedBy, userId, roleId };
      }
    }

    // Try direct message fields
    const type = message.type as PermissionStatusMessageType;
    if (type && this.validTypes.includes(type)) {
      const dataBag = (message.data as Record<string, unknown> | null | undefined) ?? {};
      const entityId = this.extractEntityId(dataBag);
      const entityType = this.extractEntityType(type);

      return {
        type,
        entityId: entityId || UNKNOWN_ENTITY_ID,
        entityType,
        data: (dataBag['data'] ?? dataBag) as Permission | Role | UserRole | undefined,
        timestamp: message.timestamp?.toISOString() || new Date().toISOString(),
        initiatedBy: (dataBag['initiatedBy'] || dataBag['initiated_by']) as string | undefined,
        userId: (dataBag['userId'] || dataBag['user_id']) as string | undefined,
        roleId: (dataBag['roleId'] || dataBag['role_id']) as string | undefined
      };
    }

    return null;
  }

  private extractType(message: WebSocketMessage): PermissionStatusMessageType {
    const data = message.data as { type?: string } | null | undefined;
    return (data?.type || message.type) as PermissionStatusMessageType;
  }

  /**
   * Determine entity type from message type
   */
  private extractEntityType(type: PermissionStatusMessageType): 'role' | 'permission' | 'user_role' {
    if (type.includes('role_created') || type.includes('role_updated') || type.includes('role_deleted')) {
      return 'role';
    }
    if (type.includes('user_role')) {
      return 'user_role';
    }
    return 'permission';
  }

  /**
   * Validate UUID format (normalizes to lowercase before testing)
   * @param id - UUID string to validate
   * @returns true if valid UUID v4 format
   */
  private isValidUuid(id: string): boolean {
    // Normalize to lowercase to handle mixed-case UUIDs from different sources
    return this.UUID_REGEX.test(id.toLowerCase());
  }

  /**
   * Extract entity ID from message data with strict priority order (DEV-1448)
   *
   * Security: Uses strict priority order to prevent ID confusion attacks where
   * an attacker could craft messages with multiple ID fields to cause the system
   * to use an unintended ID. Primary candidates (explicit entityId fields) are
   * always preferred over secondary fallbacks (type-specific IDs).
   *
   * @param data - Message data object containing potential ID fields
   * @returns Valid UUID string or undefined if no valid ID found
   */
  private extractEntityId(data: Record<string, unknown>): string | undefined {
    // Strict priority order - prefer explicit entityId fields first
    // This prevents ID confusion attacks where multiple ID fields are present
    const primaryCandidates = [data['entityId'], data['entity_id']];

    for (const candidate of primaryCandidates) {
      if (this.isValidUuidCandidate(candidate)) {
        return candidate as string;
      }
    }

    // Secondary fallback to type-specific IDs (only if no primary ID found)
    const secondaryCandidates = [
      data['roleId'],
      data['role_id'],
      data['permissionId'],
      data['permission_id'],
      data['id']
    ];

    for (const candidate of secondaryCandidates) {
      if (this.isValidUuidCandidate(candidate)) {
        return candidate as string;
      }
    }

    return undefined;
  }

  /**
   * Check if a candidate value is a valid UUID
   * @param candidate - Value to validate
   * @returns true if candidate is a string with valid UUID format
   */
  private isValidUuidCandidate(candidate: unknown): boolean {
    return typeof candidate === 'string' && this.isValidUuid(candidate);
  }

  private extractUserId(data: Record<string, unknown>): string | undefined {
    const id = data['userId'] || data['user_id'];
    if (id && typeof id === 'string' && this.isValidUuid(id)) {
      return id;
    }
    return undefined;
  }

  private extractRoleId(data: Record<string, unknown>): string | undefined {
    const id = data['roleId'] || data['role_id'];
    if (id && typeof id === 'string' && this.isValidUuid(id)) {
      return id;
    }
    return undefined;
  }

  private extractData(data: Record<string, unknown>): Role | Permission | UserRole | null {
    return (data['data'] || data['role'] || data['permission'] || data['userRole'] || null) as Role | Permission | UserRole | null;
  }

  private extractTimestamp(message: WebSocketMessage): string {
    return (message.data as Record<string, unknown>)?.['timestamp'] as string ||
           message.timestamp?.toISOString() ||
           new Date().toISOString();
  }

  private extractInitiatedBy(data: Record<string, unknown>): string | undefined {
    return (data['initiatedBy'] || data['initiated_by']) as string | undefined;
  }
}
